import {
  Cell,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Header,
  HeaderGroup,
  Row,
  RowData,
  SortingFn,
  Table as ReactTable,
  TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { getFacetedRowModel } from '@tanstack/react-table';
import clsx from 'clsx';
import React, {
  ComponentType,
  CSSProperties,
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useScroll, { State } from 'react-use/lib/useScroll';
import {
  GroupedVirtuosoHandle,
  SizeFunction,
  TableVirtuoso,
} from 'react-virtuoso';
import { ColorThreshold } from 'src/components/TableV2/cells/ConditionalCell';
import { useActiveFocusContext } from 'src/contexts/ActiveFocusContext/ActiveFocusContext';
import { useColumnResizingContext } from 'src/contexts/ColumnResizingContext/ColumnResizingContext';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { FormatOptionEntries } from 'src/modals/EditTableColumns';
import { TableTd } from 'src/tables/Table/components/TableTd';
import { PosGroupedVirtuosoTable } from 'src/tables/Table/GroupedVirtuosoTable/PosGroupedVirtuosoTable';
import { SectionType } from 'src/utils/types/sectionType';

import { ColumnHeader, PaginationFooter } from './components';
import { useColumnVirtualizer } from './hooks/useColumnVirtualizer';
import { PosVirtuosoTable } from './PosVirtuosoTableComponent/PosVirtuosoTable';
import { PosVirtuosoTableContext } from './PosVirtuosoTableComponent/PosVirtuosoTableContext';
import { PosVirtuosoTableFooter } from './PosVirtuosoTableComponent/PosVirtuosoTableFooter';
import { PosVirtuosoTableHead } from './PosVirtuosoTableComponent/PosVirtuosoTableHead';
import { PosVirtuosoTableRow } from './PosVirtuosoTableComponent/PosVirtuosoTableRow';
import * as styles from './Table.css';
import { useTableHotkeys } from './Table.hooks';
import { RowComponentWithRowData, RowWrapper } from './Table.types';
import { itemSizeDefault } from './Table.utils';

// Overscan ensures items are pre-rendered before coming into view during fast scrolling
// Low values cause blank areas during fast scroll
const virtuosoTableStyle = { width: '100%', height: '100%' };
const OverscanLength = window.innerHeight / 2;
const DefaultItemHeight = 36;

// Memoized TableVirtuoso components to prevent re-creation
const memoizedVirtuosoComponents = {
  Table: PosVirtuosoTable,
  TableHead: PosVirtuosoTableHead,
  TableRow: PosVirtuosoTableRow,
  TableFoot: PosVirtuosoTableFooter,
};
const MIN_COLUMN_COUNT_TO_USE_VIRTUOSO = 20;

export interface VirtuosoScrollEvent {
  scrollState: State;
  element: HTMLElement;
}

declare module '@tanstack/table-core' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    paddingLeft?: string;
    paddingLeftLargeDesktop?: string;
    styleOverrides?: React.CSSProperties;
    /**
     * If not specified, the default should handled as multi select
     * 'seatRange' needs to be used with filterSeatRangeFn from tableUtils.tsx as custom filter function
     */
    columnFilterVariant?:
      | 'seatRange'
      | 'numberRange'
      | 'multiSelect'
      | 'textSearch'
      | 'dateRange'
      | 'moneyRange'
      | 'boolean'
      | 'dualModeNumber'
      | 'userTag';
    /**
     * See filter functions defined in tableUtils.tsx for actual expected data shape
     *
     * 'seatingSection'
     *   Cell value expected: { seating: Seating }
     *   Filter function: filterSeatingSectionFn
     *
     * 'seatTraits'
     *   Cell value expected: ListingNote[]
     *   Filter function: filterSeatTraitsFn
     *
     */
    uniqueValueVariant?:
      | 'seatingSection'
      | 'seatTraits'
      | 'marketplaceBroadcast'
      | 'outlierMode'
      | 'outlierStdDev'
      | 'compListingMode';
  }

  interface TableMeta<TData extends RowData> {
    summaryData?: TData | null | undefined;
    sectionType?: SectionType;
    rowSupportsAccordion?: boolean;
    formatOptionOverrides?: FormatOptionEntries;
    showSelectAllCheckbox?: boolean;
    numericColorThresholds?: Record<string, ColorThreshold[]>;
  }

  // Used to use sortingFns (custom sorting) in columnDef
  // (because it doesn't other strings but the table definition does)
  interface SortingFns {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: SortingFn<any>;
  }
}

type CustomTrProps = Omit<
  React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLTableRowElement>,
    HTMLTableRowElement
  >,
  'ref'
>;

const stickyHeaderStyles: CSSProperties = {
  position: 'sticky',
  top: '0',
  zIndex: '10',
};

export type TableLayout = 'fixed' | 'auto';

export type PosTableData = RowData;

export type TableProps<T extends PosTableData> = {
  tableLayout?: TableLayout;
  options: Partial<TableOptions<T>>;
  /**
   * Custom row wrapper to wrap row content *inside* tr element
   */
  rowWrapper?: RowWrapper<T>;
  /**
   * Custom row component to render each row to *replace tr element* totally
   */
  RowComponent?: ComponentType<RowComponentWithRowData<T>>;
  style?: CSSProperties;
  tableStyle?: CSSProperties;
  tableHeadStyle?: CSSProperties;
  tableHeadCellStyle?: {
    paddingTop?: string | null;
    paddingBottom?: string | null;
    fontSize?: string | null;
    fontWeight?: number | null;
  };
  tableCellStyle?: CSSProperties;
  tableRowStyle?: CSSProperties;
  roundBorders?: boolean;
  showPageSizeOptions?: boolean;
  withOuterPadding?: boolean;
  className?: string;
  useVirtuoso?: boolean;
  useStickyHeader?: boolean;
  useStickyFooter?: boolean;
  virtuosoItemSize?: SizeFunction;
  resizeDisabled?: boolean;
  /**
   * By default, footer row will show up whenever there's any footer component
   * defined in the table columns config
   *
   * Set this to true to disable the footer row even if there are footer components
   */
  disableFooter?: boolean;
  usePaginationFooter?: boolean;
  isV2FooterVariant?: boolean;
  /**
   * Use this to enable column filters
   * Tanstack makes the default value true
   * But most our columns don't need it yet, so we make it false by default
   * If any column of a table needs it, it can be enabled by setting this prop to true
   */
  enableColumnFilters?: boolean;

  // Grouping
  useDataGrouping?: boolean;
  /**
   * Expected height of items in table with data grouping
   */
  dataGroupingItemHeight?: number;
  // Table hotkeys
  useTableNavKeys?: boolean;
  // Row select in hotkey
  onRowHotkeySelect?: (row: Row<T>) => void;
  // Different from row selection, active rows
  activeRowIds?: Set<string>;
  // The proposed row selection callback
  onRowSelect?: (
    tableRows: Row<T>[] | undefined,
    row: Row<T>,
    index: number
  ) => void;
  /***
   * @deprecated, remove when TableRowSelectionV2 is GA'ed
   */
  onRowShiftSelect?: (
    tableRows: Row<T>[] | undefined,
    row: Row<T>,
    index: number
  ) => void;
  onVirtuosoEndReached?: (lastItemIndex?: number) => void;
  onFilteredRowsChange?: (filteredRows: Row<T>[] | undefined) => void;
  footerEl?: ReactNode;
  itemsRendered?: (items: Row<T>[]) => void;
  disableColumnVirtualization?: boolean;
  /**
   * Implemented for useDataGrouping only
   */
  scrollToItemRowId?: string;
  /**
   * Flag to use TableRowSelectionV2, should be cleaned when feature is enabled
   */
  rowSelectionV2?: boolean;
};

export const Table = <T extends PosTableData>({
  tableLayout,
  options,
  rowWrapper,
  RowComponent,
  tableStyle,
  tableHeadStyle,
  tableHeadCellStyle,
  tableCellStyle,
  tableRowStyle,
  showPageSizeOptions,
  withOuterPadding,
  className,
  useVirtuoso,
  useStickyHeader,
  useStickyFooter,
  virtuosoItemSize,
  resizeDisabled,
  disableFooter,
  usePaginationFooter = true,
  useDataGrouping,
  dataGroupingItemHeight = 40,
  useTableNavKeys = false,
  activeRowIds,
  onRowHotkeySelect,
  onRowSelect,
  onRowShiftSelect,
  onVirtuosoEndReached,
  onFilteredRowsChange,
  footerEl,
  roundBorders,
  enableColumnFilters = false,
  isV2FooterVariant = false,
  itemsRendered,
  disableColumnVirtualization,
  scrollToItemRowId,
  rowSelectionV2,
}: TableProps<T>) => {
  let rowIndex = 0;
  const isLargeDesktop = useMatchMedia('largeDesktop');
  const tableWrapperRef = useRef<HTMLTableElement | null>(null);
  const groupedVirtuosoRef = useRef<GroupedVirtuosoHandle>(null);
  const { isResizeEnabled, customColumnWidths, onColumnSizingChange } =
    useColumnResizingContext();
  const { activeItem, onKeyDownHandler } = useActiveFocusContext();

  if (useStickyHeader) {
    tableHeadStyle = {
      ...stickyHeaderStyles,
      ...tableHeadStyle,
    };
  }

  const table: ReactTable<T> = useReactTable({
    autoResetPageIndex: false,
    enableColumnResizing: isResizeEnabled && !resizeDisabled,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: useDataGrouping ? getGroupedRowModel() : undefined,
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: usePaginationFooter ? getPaginationRowModel() : null,
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: options.getFacetedRowModel ?? getFacetedRowModel(),
    getFacetedUniqueValues:
      options.getFacetedUniqueValues ?? getFacetedUniqueValues(),
    onColumnSizingChange: onColumnSizingChange,
    // This is default turned on, but we want to turn it off in most cases
    enableRowSelection: false,
    // This is default turned on, but we want to turn it off in most cases
    enableMultiRowSelection: false,
    enableColumnFilters: enableColumnFilters,
    filterFromLeafRows: enableColumnFilters,
    filterFns: {},

    // Leave this in false to not reorder columns when grouping
    groupedColumnMode: false,
    ...options,
    state: {
      ...options.state,
      columnSizing: customColumnWidths ?? {},
    },
  } as TableOptions<T>);

  const isGrouping = table.getState().grouping?.length > 0;
  const tableRows = table.getRowModel().rows;
  const footerRow = table.getFooterGroups();
  const hasFooterDef = footerRow.some((row: HeaderGroup<T>) =>
    row.headers.some(
      (header: Header<T, unknown>) => header.column.columnDef.footer != null
    )
  );

  const showFooter = hasFooterDef && !disableFooter;

  // Listen to filtered rows change and trigger callback
  useEffect(() => {
    if (onFilteredRowsChange == null) return;

    const hasColumnFilterApplied = table
      .getAllColumns()
      .some((column) => column.getFilterValue() != null);

    // Make it undefined when no filter is applied
    const filteredRows = hasColumnFilterApplied
      ? table.getFilteredRowModel().flatRows
      : undefined;

    onFilteredRowsChange(filteredRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.getFilteredRowModel().flatRows]);

  useTableHotkeys(useTableNavKeys, table, onRowHotkeySelect);

  useEffect(() => {
    if (!activeItem) return;
    tableRows[activeItem.index]?.toggleSelected(
      Array.from(activeItem.id)[0] !== '-'
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableRows, activeItem]);

  const scrollRef = useRef<HTMLElement | null>(null);
  const scrollState = useScroll(scrollRef);

  const renderRowCell = useCallback(
    (cell: Cell<T, unknown>) => {
      return (
        <TableTd
          key={cell.id}
          isLargeDesktop={isLargeDesktop}
          isGrouping={isGrouping}
          isResizeEnabled={isResizeEnabled}
          cell={cell}
          tableCellStyle={tableCellStyle}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableTd>
      );
    },
    [isGrouping, isLargeDesktop, isResizeEnabled, tableCellStyle]
  );

  const renderRowContent = useCallback(
    (row: Row<T>) => {
      return (
        <>
          {row.getVisibleCells().map((cell) => {
            return renderRowCell(cell);
          })}
        </>
      );
    },
    [renderRowCell]
  );

  const renderHeader = useCallback(
    (header: Header<T, unknown>) => (
      <ColumnHeader
        key={header.id}
        header={header}
        table={table}
        style={tableHeadCellStyle}
      />
    ),
    [table, tableHeadCellStyle]
  );

  const renderFooter = useCallback(
    (cell: Header<T, unknown>) => (
      <td
        key={cell.id}
        style={{
          paddingLeft: isLargeDesktop
            ? cell.column.columnDef.meta?.paddingLeftLargeDesktop
            : cell.column.columnDef.meta?.paddingLeft,
          ...tableCellStyle,
        }}
      >
        {flexRender(cell.column.columnDef.footer, cell.getContext())}
      </td>
    ),
    [isLargeDesktop, tableCellStyle]
  );

  const {
    renderHeaderRow,
    renderRowContent: renderRowContentVirtualized,
    renderFooterRowContent,
  } = useColumnVirtualizer(
    table,
    scrollRef,
    renderHeader,
    renderRowCell,
    renderFooter
  );

  const [isInitialScrollDone, setIsInitialScrollDone] = useState(false);

  useEffect(() => {
    if (useDataGrouping) {
      if (
        !isInitialScrollDone &&
        scrollToItemRowId != null &&
        groupedVirtuosoRef.current
      ) {
        setTimeout(() => {
          const tableRows = table.getRowModel().flatRows;

          const scrollToItemIndex = tableRows.findIndex(
            (r) => r.id === scrollToItemRowId
          );

          if (scrollToItemIndex === -1) return;

          const groupItemIndex = tableRows.findLastIndex(
            (r, i) => r.depth === 0 && i <= scrollToItemIndex
          );

          const groupIndex =
            tableRows.slice(0, groupItemIndex + 1).filter((r) => r.depth === 0)
              .length - 1;

          if (groupIndex === -1) return;

          // What this is doing: First scroll to groupIndex (accurate) and then scroll
          // down a bit to try to get the item we want into view
          // NOTE: This is a bit hacky and not 100% accurate because `dataGroupingItemHeight` is not always accurate
          // But it's the best we can do without a major refactor of how grouping works
          // and works better than specifying item index in the virtuoso `scrollToIndex` directly
          groupedVirtuosoRef.current?.scrollToIndex({
            groupIndex: groupIndex,
            offset:
              dataGroupingItemHeight * (scrollToItemIndex - groupItemIndex - 2),
          });
        }, 350);

        setIsInitialScrollDone(true);
      }
    }
  }, [
    scrollToItemRowId,
    table,
    useVirtuoso,
    groupedVirtuosoRef,
    useDataGrouping,
    isInitialScrollDone,
    dataGroupingItemHeight,
  ]);

  const useColumnVirtualization = useMemo(
    () =>
      useVirtuoso &&
      table.getVisibleLeafColumns().length >=
        MIN_COLUMN_COUNT_TO_USE_VIRTUOSO &&
      !disableColumnVirtualization,
    [useVirtuoso, table, disableColumnVirtualization]
  );

  const renderRowContentWithWrapper = useCallback(
    (row: Row<T>) => {
      const rowContent = useColumnVirtualization
        ? renderRowContentVirtualized(row)
        : renderRowContent(row);

      const children = rowWrapper
        ? rowWrapper(row.original, rowContent, rowIndex++)
        : rowContent;

      return children;
    },
    [
      renderRowContent,
      renderRowContentVirtualized,
      rowIndex,
      rowWrapper,
      useColumnVirtualization,
    ]
  );

  // Memoize TableVirtuoso context to prevent unnecessary re-renders
  const virtuosoContext = useMemo(
    () => ({
      tableLayout,
      tableStyle,
      tableHeadStyle,
      tableRowStyle,
      tableRows,
      rowWrapper,
      RowComponent,
      useStickyFooter,
      activeRowIds,
      onRowSelect,
      onRowShiftSelect,
    }),
    [
      tableLayout,
      tableStyle,
      tableHeadStyle,
      tableRowStyle,
      tableRows,
      rowWrapper,
      RowComponent,
      useStickyFooter,
      activeRowIds,
      onRowSelect,
      onRowShiftSelect,
    ]
  );

  // Memoize scroll handler
  const scrollerRefHandler = useCallback((el: HTMLElement | Window | null) => {
    if (el instanceof HTMLElement) scrollRef.current = el;
  }, []);

  // Memoize item content renderer for TableVirtuoso with better performance
  const itemContentRenderer = useCallback(
    (index: number, row: Row<T>) => {
      if (!row) return null;
      return useColumnVirtualization
        ? renderRowContentVirtualized(row)
        : renderRowContent(row);
    },
    [useColumnVirtualization, renderRowContentVirtualized, renderRowContent]
  );

  // Memoize fixed header content
  const fixedHeaderContent = useCallback(() => {
    return useColumnVirtualization ? (
      renderHeaderRow()
    ) : (
      <>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <>{renderHeader(header)}</>
            ))}
          </tr>
        ))}
      </>
    );
  }, [useColumnVirtualization, renderHeaderRow, table, renderHeader]);

  // Memoize fixed footer content
  const fixedFooterContent = useCallback(
    () => (
      <>
        {footerEl}
        {showFooter && (
          <>
            {footerRow.map((row, index) => {
              const tableRow = (customProps?: CustomTrProps) => (
                <tr
                  className={styles.tableFooterRow}
                  key={`footer-${row.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  onKeyDown={
                    activeItem != null
                      ? (e) => onKeyDownHandler(e, index, tableRows)
                      : undefined
                  }
                  {...customProps}
                  style={tableRowStyle}
                >
                  {useColumnVirtualization ? (
                    renderFooterRowContent(row)
                  ) : (
                    <>
                      {row.headers.map((cell) => (
                        <>{renderFooter(cell)}</>
                      ))}
                    </>
                  )}
                </tr>
              );
              return tableRow();
            })}
          </>
        )}
      </>
    ),
    [
      footerEl,
      showFooter,
      useColumnVirtualization,
      renderFooterRowContent,
      footerRow,
      activeItem,
      tableRowStyle,
      onKeyDownHandler,
      tableRows,
      renderFooter,
    ]
  );

  // We cannot used fixed layout if we're using column virtualization
  if (useColumnVirtualization) {
    tableLayout = 'auto';
  }

  return (
    <div
      ref={tableWrapperRef}
      className={clsx(
        styles.POSTableContainer,
        useVirtuoso && styles.POSTableVirtuosoContainer,
        withOuterPadding && styles.ContainerPadding,
        roundBorders && styles.roundBorders,
        className
      )}
    >
      {useDataGrouping ? (
        <PosGroupedVirtuosoTable
          renderRowContent={renderRowContentWithWrapper}
          virtuosoRef={groupedVirtuosoRef}
          scrollRef={scrollRef}
          itemsRendered={itemsRendered}
          endReached={onVirtuosoEndReached}
          context={{
            tableRows,
            RowComponent,
            table,
            scrollY: scrollState.y,
            useDataGrouping,
            showFooter,
            activeRowIds,
            rowSelectionV2,
            onRowSelect,
            onRowShiftSelect,
          }}
        />
      ) : useVirtuoso ? (
        <TableVirtuoso<Row<T>, PosVirtuosoTableContext<T>>
          style={virtuosoTableStyle}
          data={tableRows}
          overscan={{ main: OverscanLength, reverse: OverscanLength }}
          increaseViewportBy={{ top: OverscanLength, bottom: OverscanLength }}
          defaultItemHeight={DefaultItemHeight}
          totalCount={tableRows.length}
          context={virtuosoContext}
          scrollerRef={scrollerRefHandler}
          endReached={onVirtuosoEndReached}
          itemSize={virtuosoItemSize ?? itemSizeDefault}
          // Always use components declared outside a rendering component
          // or new elements are created causing to lose state
          // https://virtuoso.dev/customize-structure/
          components={memoizedVirtuosoComponents}
          fixedHeaderContent={fixedHeaderContent}
          fixedFooterContent={fixedFooterContent}
          itemContent={itemContentRenderer}
        />
      ) : (
        <div
          style={{ overflow: 'auto' }}
          ref={scrollRef as MutableRefObject<HTMLDivElement | null>}
        >
          <table
            className={styles.table}
            style={{ tableLayout, ...tableStyle }}
          >
            <thead className={styles.tableHead} style={tableHeadStyle}>
              {useColumnVirtualization
                ? renderHeaderRow()
                : table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <ColumnHeader
                          key={header.id}
                          header={header}
                          table={table}
                          style={tableHeadCellStyle}
                        />
                      ))}
                    </tr>
                  ))}
            </thead>
            <tbody>
              {tableRows.map((row, index) => {
                const isSelected = rowSelectionV2
                  ? Boolean(activeRowIds?.has(row.id))
                  : row.getIsSelected();
                const rowProps = {
                  className: clsx(styles.tableRow, {
                    [styles.isSelectedRow]: isSelected,
                    [styles.isSubRow]: row.depth > 0,
                  }),
                  key: row.id,
                  onClick: (e: React.MouseEvent) => {
                    e.stopPropagation();
                    if (rowSelectionV2) {
                      onRowSelect?.(tableRows, row, index);
                    } else {
                      row.toggleSelected();
                      onRowShiftSelect?.(tableRows, row, index);
                    }
                  },
                  onKeyDown: activeItem
                    ? (e: React.KeyboardEvent) =>
                        onKeyDownHandler(e, index, tableRows)
                    : undefined,
                  tabIndex:
                    activeItem && activeItem.id === row.id
                      ? 0
                      : row.getIsSelected()
                        ? 0
                        : -1,
                  style: tableRowStyle,
                };
                const rowContent = useColumnVirtualization
                  ? renderRowContentVirtualized(row)
                  : renderRowContent(row);

                const children = rowWrapper
                  ? rowWrapper(row.original, rowContent, rowIndex++)
                  : rowContent;

                const tableRow = RowComponent ? (
                  <RowComponent {...rowProps} row={row}>
                    {children}
                  </RowComponent>
                ) : (
                  <tr {...rowProps}>{children}</tr>
                );

                return tableRow;
              })}
              {showFooter &&
                footerRow.map((row, index) => {
                  const tableRow = (customProps?: CustomTrProps) => (
                    <tr
                      className={styles.tableFooterRow}
                      key={`footer-${row.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      onKeyDown={
                        activeItem != null
                          ? (e) => onKeyDownHandler(e, index, tableRows)
                          : undefined
                      }
                      {...customProps}
                      style={tableRowStyle}
                    >
                      {row.headers.map((cell) => (
                        <td
                          key={cell.id}
                          style={{
                            paddingLeft: isLargeDesktop
                              ? cell.column.columnDef.meta
                                  ?.paddingLeftLargeDesktop
                              : cell.column.columnDef.meta?.paddingLeft,
                            ...tableCellStyle,
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.footer,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  );

                  return tableRow();
                })}
              {footerEl}
            </tbody>
          </table>
        </div>
      )}
      {usePaginationFooter && table.getPageCount() > 1 && (
        <PaginationFooter
          table={table}
          tableWrapperRef={tableWrapperRef}
          showPageSizeOptions={showPageSizeOptions}
          isV2Variant={isV2FooterVariant}
        />
      )}
    </div>
  );
};

export default Table;
